<script setup>
  import {computed, inject, onMounted, onUnmounted, ref, watch} from "vue";

  import DeveloperContainer from "@components/_DevOnly/DeveloperContainer.vue";
  import ComponentBadge from "@components/Badges/Badge.vue";
  import ComponentAiSummaryButton from "@components/Buttons/AiSummaryButton.vue";
  import ComponentCommentsDialog from "@components/Dialogs/CommentsDialog.vue";
  import ComponentEditor from "@components/Editor/Editor.vue";
  import {useForm} from "@inertiajs/vue3";
  import PagesRecordResultListClaimInteractionChat from "@pages/Records/Components/Result/ResultList/ClaimInteractionChat.vue";
  import {MedBadge, MedButton, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {usePrivacyGuard} from "@utils/Helpers/PrivacyGuard.js";

  defineOptions({
    name: "PagesRecordResultSummary",
  });

  const props = defineProps({
    record: {
      type: Object,
      required: true,
    },
    patient: {
      type: Object,
      required: true,
    },
    checks: {
      type: Array,
      default: null,
    },
  });

  defineEmits(["open:shareDialog"]);

  const {validatePrivacyObject} = usePrivacyGuard();

  const refCommentsDialog = ref(null);
  const refConfidentialCommentsDialog = ref(null);
  const activeTab = ref("tab-doctor");

  const activeInteractionTab = ref("tab-chat");
  const isFloating = ref(false);
  const visibleTabPanels = ref(true);

  const comments = inject("comments");
  const summaryComments = computed(() => {
    return comments.value.filter((comment) => comment.section === "default" && comment.confidential === false);
  });
  const summaryConfidentialComments = computed(() => {
    return comments.value.filter((comment) => comment.section === "default" && comment.confidential === true);
  });

  const form = useForm({
    doctorsummary: props.record.doctorsummary,
    patientsummary: props.record.patientsummary,
    usersummary: props.record.usersummary,
  });

  watch(
    () => props.record,
    function (newRecord) {
      form.doctorsummary = newRecord.doctorsummary;
      form.patientsummary = newRecord.patientsummary;
      form.usersummary = newRecord.usersummary;
    },
  );

  function save() {
    form.patch(route("records.update", {patient: props.record.patient_id, record: props.record.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "record"],
    });
  }

  function changeTab(newTab) {
    activeTab.value = newTab;
  }

  const handleResizeBreakpoint = (event) => {
    isFloating.value =
      event.detail.breakpoint === null ||
      event.detail.breakpoint === "xs" ||
      event.detail.breakpoint === "sm" ||
      event.detail.breakpoint === "md";
    if (!isFloating.value) {
      visibleTabPanels.value = true;
    }
  };

  onMounted(() => {
    globalThis.addEventListener("breakpoint", handleResizeBreakpoint);
  });

  onUnmounted(() => {
    globalThis.removeEventListener("breakpoint", handleResizeBreakpoint);
  });

  function changeInteractionTab(newTab) {
    if (isFloating.value && activeInteractionTab.value === newTab) {
      visibleTabPanels.value = !visibleTabPanels.value;
    } else {
      visibleTabPanels.value = true;
      activeInteractionTab.value = newTab;
    }
  }

  function openCommentsDialog() {
    refCommentsDialog.value.open();
  }

  function openConfidentialCommentsDialog() {
    refConfidentialCommentsDialog.value.open();
  }

  const privacyErrors = computed(() => {
    return validatePrivacyObject(form, {
      doctorsummary: "Zusammenfassung an Arzt",
      patientsummary: "Zusammenfassung an Patienten",
      usersummary: "Notizen zur Analyse",
    });
  });
</script>

<template>
  <section class="print:hidden">
    <h2 class="border-b-1 border-gray-200 pb-6 text-center text-2xl font-semibold print:font-normal">
      Zusammenfassung der Analyse (optional)
    </h2>

    <div class="relative flex items-start space-x-8 p-2">
      <main class="flex-1">
        <nav>
          <ul class="flex flex-wrap space-x-2 border-b border-gray-200">
            <li>
              <button
                :class="
                  cn('rounded-t-md p-2 font-semibold text-gray-900 hover:bg-gray-100 enabled:cursor-pointer', {
                    'border-b-2 border-mcred bg-mcred-100/50 text-mcred hover:bg-mcred-100 hover:text-mcred-700':
                      activeTab === 'tab-doctor',
                  })
                "
                :data-test="getTestId('summary-tab-doc-button')"
                @click="changeTab('tab-doctor')"
              >
                an Arzt
              </button>
            </li>

            <li>
              <button
                :class="
                  cn('rounded-t-md p-2 font-semibold text-gray-900 hover:bg-gray-100 enabled:cursor-pointer', {
                    'border-b-2 border-mcred bg-mcred-100/50 text-mcred hover:bg-mcred-100 hover:text-mcred-700':
                      activeTab === 'tab-patient',
                  })
                "
                :data-test="getTestId('summary-tab-patient-button')"
                @click="changeTab('tab-patient')"
              >
                an Patienten
              </button>
            </li>

            <li>
              <button
                :class="
                  cn('rounded-t-md p-2 font-semibold text-gray-900 hover:bg-gray-100 enabled:cursor-pointer', {
                    'border-b-2 border-mcred bg-mcred-100/50 text-mcred hover:bg-mcred-100 hover:text-mcred-700':
                      activeTab === 'tab-archive',
                  })
                "
                :data-test="getTestId('summary-tab-archive-button')"
                @click="changeTab('tab-archive')"
              >
                Notizen
              </button>
            </li>
          </ul>
        </nav>

        <div class="mt-3">
          <div v-if="activeTab === 'tab-doctor'" role="tabpanel">
            <div class="flex flex-row justify-end">
              <MedTooltip
                v-if="$page.props.client.license.is_pro && summaryConfidentialComments.length > 0"
                content="Rückmeldung des Arztes"
              >
                <MedButton
                  flat
                  variant="primary"
                  class="text-sm"
                  :data-test="getTestId('confidential-comments-button')"
                  @click="openConfidentialCommentsDialog()"
                >
                  <ComponentBadge icon-class="-top-3 -right-3">
                    <template #icon>{{ summaryConfidentialComments.length }}</template>
                    Arztrückmeldung
                  </ComponentBadge>
                </MedButton>
              </MedTooltip>
            </div>

            <ComponentEditor
              key="doctorSummary"
              v-model="form.doctorsummary"
              class="my-0 pb-0"
              label="Ihre eigene Zusammenfassung an Fachkreise:"
              placeholder="Zusammenfassung für PDF an Arzt"
              :maxlength="50000"
              :privacy-guard="privacyErrors.doctorsummary"
              :test-id="getTestId('summary-tab-doc')"
              @blur="save()"
            />

            <div class="flex justify-end gap-1 print:hidden">
              <DeveloperContainer>
                <MedButton
                  variant="dev"
                  as="a"
                  target="_blank"
                  :href="$route('ai-debugger.index') + '?id=' + record.id + '&feature=summary&target=doctor'"
                  :data-test="getTestId('ai-debug-summary-doctor-button')"
                >
                  AI Debugger (summary/doctor)
                </MedButton>
              </DeveloperContainer>

              <ComponentAiSummaryButton target="doctor" :record="record" :patient="patient" :checks="checks" />
            </div>
          </div>

          <div v-if="activeTab === 'tab-patient'" role="tabpanel">
            <ComponentEditor
              key="patientSummary"
              v-model="form.patientsummary"
              class="my-0 pb-0"
              label="Ihre eigene Zusammenfassung an den Patienten:"
              placeholder="Zusammenfassung für PDF an Patienten"
              :maxlength="50000"
              :privacy-guard="privacyErrors.patientsummary"
              :test-id="getTestId('summary-tab-patient')"
              @blur="save()"
            />

            <div class="flex justify-end gap-1 print:hidden">
              <DeveloperContainer>
                <MedButton
                  variant="dev"
                  as="a"
                  target="_blank"
                  :href="$route('ai-debugger.index') + '?id=' + record.id + '&feature=summary&target=patient'"
                  :data-test="getTestId('ai-debug-summary-patient-button')"
                >
                  AI Debugger (summary/patient)
                </MedButton>
              </DeveloperContainer>

              <ComponentAiSummaryButton target="patient" :record="record" :patient="patient" :checks="checks" />
            </div>
          </div>

          <div v-if="activeTab === 'tab-archive'" role="tabpanel">
            <ComponentEditor
              key="archiveSummary"
              v-model="form.usersummary"
              class="my-0 pb-0"
              label="Notizen zur Analyse werden in das PDF für Ihre eigene Ablage/Archiv eingefügt:"
              placeholder="Interne Notizen"
              :maxlength="50000"
              :privacy-guard="privacyErrors.usersummary"
              :test-id="getTestId('summary-tab-archive')"
              @blur="save()"
            />
          </div>
        </div>
      </main>

      <!-- TODO Fix mobile layout wtf -->
      <aside class="absolute right-0 w-1/4 min-w-64 bg-white pl-2 shadow-lg lg:relative lg:pl-0 lg:shadow-none">
        <nav>
          <ul class="flex flex-wrap space-x-2 border-b border-gray-200">
            <li class="flex-1" />

            <li>
              <MedBadge :label="summaryComments?.length" :data-test="getTestId('summary-tab-chat-badge')">
                <button
                  :class="
                    cn(
                      'inline-block rounded-t-md border-b-2 border-mcred bg-mcred-100/50 p-2 font-semibold text-mcred hover:bg-mcred-100 hover:text-mcred-700 enabled:cursor-pointer',
                    )
                  "
                  @click="changeInteractionTab('tab-chat')"
                >
                  Team
                </button>
              </MedBadge>
            </li>
          </ul>
        </nav>

        <div v-if="visibleTabPanels" class="mt-3">
          <div role="tabpanel">
            <PagesRecordResultListClaimInteractionChat
              :comments="summaryComments"
              comment-section="default"
              :patient-id="record.patient_id"
              :record-id="record.id"
              :hide-share="true"
              @open:comments-dialog="openCommentsDialog()"
              @open:share-dialog="$emit('open:shareDialog')"
            />
          </div>
        </div>
      </aside>
    </div>

    <ComponentCommentsDialog
      ref="refCommentsDialog"
      :record-id="record.id"
      :patient-id="record.patient_id"
      :confidential="false"
      :comments="summaryComments"
    />

    <ComponentCommentsDialog
      ref="refConfidentialCommentsDialog"
      :record-id="record.id"
      :patient-id="record.patient_id"
      :confidential="true"
      :comments="summaryConfidentialComments"
    />
  </section>
</template>
